import type { NoteView } from "@screener/protocol";
import { useCallback, useEffect, useState } from "react";
import {
  createNote,
  deleteNote,
  fetchNotes,
  type NoteWriteResult,
  updateNoteText,
} from "./notes-api";

/**
 * Состояние списка заметок §6.4. «Недоступно» — отдельное от пустого списка
 * (web-density-detail «Список заметок не прочитан»): пустой журнал и
 * непрочитанный журнал говорят разное.
 */
export type NotesState =
  | { status: "loading" }
  | { status: "ready"; notes: NoteView[] }
  | { status: "unavailable" };

interface UseNotesOptions {
  /** Сессия истекла — тот же исход, что у потока и деталей плотности. */
  onSessionInvalid: () => void;
}

interface UseNotesResult {
  state: NotesState;
  create: (base: string, text: string) => Promise<NoteWriteResult>;
  updateText: (id: string, text: string) => Promise<NoteWriteResult>;
  remove: (id: string) => Promise<"ok" | "not-found" | "unavailable">;
}

/**
 * Состояние перед перечитыванием списка: уже прочитанный список не прячется
 * на время обновления — тем же приёмом держится последний осевший ответ в
 * `density-detail-content.tsx` (`lastReadyRef`). Иначе каждое собственное
 * действие (своя же запись, правка, удаление) на время своего `GET` гасило
 * бы значки по всей таблице и роняло фокус в разделе заметок.
 */
export function beforeNotesReload(previous: NotesState): NotesState {
  return previous.status === "ready" ? previous : { status: "loading" };
}

/**
 * Свои заметки §6.4 (личные с ТЗ 1.115, §134) списком: загрузка при входе,
 * перезагрузка после собственного действия, без периодического опроса
 * (design.md решение 4) — правка из другой вкладки станет видна при
 * следующем чтении.
 */
export function useNotes({ onSessionInvalid }: UseNotesOptions): UseNotesResult {
  const [state, setState] = useState<NotesState>({ status: "loading" });

  const load = useCallback(() => {
    setState(beforeNotesReload);
    fetchNotes().then((result) => {
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return;
      }
      setState(
        result.status === "ok"
          ? { status: "ready", notes: result.notes }
          : { status: "unavailable" },
      );
    });
  }, [onSessionInvalid]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(
    async (base: string, text: string): Promise<NoteWriteResult> => {
      const result = await createNote({ base, text });
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return result;
      }
      if (result.status === "ok") {
        load();
      }
      return result;
    },
    [load, onSessionInvalid],
  );

  const updateText = useCallback(
    async (id: string, text: string): Promise<NoteWriteResult> => {
      const result = await updateNoteText(id, { text });
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return result;
      }
      if (result.status === "ok") {
        load();
      }
      return result;
    },
    [load, onSessionInvalid],
  );

  const remove = useCallback(
    async (id: string): Promise<"ok" | "not-found" | "unavailable"> => {
      const result = await deleteNote(id);
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return "unavailable";
      }
      if (result.status === "ok") {
        load();
      }
      return result.status;
    },
    [load, onSessionInvalid],
  );

  return { state, create, updateText, remove };
}
